import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { xorWith, isEqual, isEmpty } from 'lodash-es'
import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { AgxRow, AgxColumn, AgxSortableList } from '@urbanx/agx-ui-components'
import {
  AgxButton,
  AgxCheckbox,
  AgxDropdown,
  AgxSpacer,
  AgxTextArea,
  AgxTextInput,
  AgxTypography,
  DropdownOption,
  Icon_Arrow,
  Icon_MagnifyingGlass,
} from '@urbanx/agx.ui-components2'
import { AgencyAgentModel } from '@urbanx/agx.agencies.client'
import {
  MarketingItemTemplateModel,
  MarketingItemTemplateModelItemType,
  MarketingPackageTemplateModel,
  useCreateMarketingPackageTemplate,
  useDeleteMarketingPackageTemplate,
  useInvalidateUseStaffGetMarketingPackages,
  useInvalidateUseStaffGetMarketingPackageTemplates,
  useUpdateMarketingPackageTemplate,
} from '@urbanx/agx.marketing.client'
import Popup from 'components/Popup/Popup'
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation'
import { FormPrompt } from 'components/FormPrompt'
import './marketingPackages.scss'

interface EditMarketingProps {
  onBack: () => void
  marketingPackage: MarketingPackageTemplateModel | undefined
  marketingPackageOrder: number
  includedStandardInclusions: string[]
  includedAddons: string[]
  allItems: MarketingItemTemplateModel[]
  agencyId: string
  allAvailableAgents: AgencyAgentModel[]
}

interface MarketingPackageFormSnapshot {
  packageName: string
  packageDisplayName: string
  specialConditions: string
  userAccessIds: string[]
  isItemisedPricing: boolean
  standardItemIds: string[]
  addonIds: string[]
}

const enum InclusionsType {
  STANDARD = 'standard',
  ADDONS = 'addons',
}

const STANDARD_INCLUSIONS_TITLE = 'Select standard inclusions to add'
const ADDON_INCLUSIONS_TITLE = 'Select add-ons to add'
const REA_LISTING_LABEL = 'REA Listing'
const DOMAIN_LISTING_LABEL = 'Domain Listing'
const SELECT_ALL_LABEL = 'Select All'
const REA_LISTING: MarketingItemTemplateModel = {
  id: REA_LISTING_LABEL,
  productName: REA_LISTING_LABEL,
  itemType: MarketingItemTemplateModelItemType.AgentToFill,
}
const DOMAIN_LISTING: MarketingItemTemplateModel = {
  id: DOMAIN_LISTING_LABEL,
  productName: DOMAIN_LISTING_LABEL,
  itemType: MarketingItemTemplateModelItemType.AgentToFill,
}
const SELECT_ALL: MarketingItemTemplateModel = {
  id: SELECT_ALL_LABEL,
  productName: SELECT_ALL_LABEL,
  itemType: MarketingItemTemplateModelItemType.AgentToFill,
}
const NEW_PACKAGE_KEY = 'new'
const UNKNOWN_AGENT_PREFIX = 'Unknown user'

const EditMarketingPackage = (props: EditMarketingProps) => {
  const {
    onBack,
    marketingPackage,
    marketingPackageOrder,
    includedStandardInclusions,
    includedAddons,
    allItems,
    agencyId,
    allAvailableAgents,
  } = props
  const [standardItems, setStandardItems] = useState<
    MarketingItemTemplateModel[]
  >([])
  const [addons, setAddons] = useState<MarketingItemTemplateModel[]>([])
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [packageName, setPackageName] = useState('')
  const [packageDisplayName, setPackageDisplayName] = useState('')
  const [addInclusionsTitle, setAddInclusionsTitle] = useState('')
  const [specialConditions, setSpecialConditions] = useState('')
  const [showEditInclusions, setShowEditInclusions] = useState(false)
  const [isItemisedPricing, setIsItemisedPricing] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [forceCheck, setForceCheck] = useState(false)
  const [standardItemsSelectAll, setStandardItemsSelectAll] = useState(false)
  const [addonsSelectAll, setAddonsSelectAll] = useState(false)
  const [isStandardItemsIndeterminate, setIsStandardItemsIndeterminate] =
    useState(false)
  const [isAddonsIndeterminate, setIsAddonsIndeterminate] = useState(false)
  const [initialFormSnapshot, setInitialFormSnapshot] =
    useState<MarketingPackageFormSnapshot | null>(null)
  const [userAccessIds, setUserAccessIds] = useState<string[]>([])
  const packageIdentity = marketingPackage?.id ?? NEW_PACKAGE_KEY

  const availableAgentOptions: DropdownOption[] = useMemo(() => {
    return allAvailableAgents.flatMap((agent) => {
      if (!agent.id) {
        return []
      }

      const fullName = `${agent.name?.firstName ?? ''} ${
        agent.name?.lastName ?? ''
      }`.trim()

      return [
        {
          value: agent.id,
          label: fullName || `${UNKNOWN_AGENT_PREFIX} (${agent.id})`,
        },
      ]
    })
  }, [allAvailableAgents])

  const allAgentOptions: DropdownOption[] = useMemo(() => {
    const availableAgentValues = new Set(
      availableAgentOptions.map((option) => option.value)
    )
    const unavailableSelectedOptions = userAccessIds
      .filter((id) => !!id && !availableAgentValues.has(id))
      .map((id) => ({
        value: id,
        label: `${UNKNOWN_AGENT_PREFIX} (${id})`,
      }))

    return [...availableAgentOptions, ...unavailableSelectedOptions]
  }, [availableAgentOptions, userAccessIds])

  const inclusionsList = useMemo(() => {
    return allItems
      .filter((i) => !addons.find((a) => a.id === i.id))
      .sort((a, b) => {
        const sortedItems = standardItems.map((item) => item.id)
        return sortedItems.indexOf(a.id) - sortedItems.indexOf(b.id)
      })
      .reduce(
        (acc, curr) => {
          acc.push(curr)
          return acc
        },
        [SELECT_ALL, REA_LISTING, DOMAIN_LISTING]
      )
  }, [allItems, addons])

  const addonsList = useMemo(() => {
    return allItems
      .filter((i) => !standardItems.find((s) => s.id === i.id))
      .sort((a, b) => {
        const sortedAddons = addons.map((addon) => addon.id)
        return sortedAddons.indexOf(a.id) - sortedAddons.indexOf(b.id)
      })
      .reduce(
        (acc, curr) => {
          acc.push(curr)
          return acc
        },
        [SELECT_ALL]
      )
  }, [allItems, standardItems])

  const invalidateUseStaffGetMarketingPackages =
    useInvalidateUseStaffGetMarketingPackages()
  const invalidateUseStaffGetMarketingPackageTemplates =
    useInvalidateUseStaffGetMarketingPackageTemplates()

  const {
    updateMarketingPackageTemplate,
    updateMarketingPackageTemplatePending,
  } = useUpdateMarketingPackageTemplate({
    onSuccess: async () => {
      setIsFormDirty(false)
      await Promise.allSettled([
        invalidateUseStaffGetMarketingPackages(agencyId),
        invalidateUseStaffGetMarketingPackageTemplates(agencyId),
      ])
      onBack()
    },
    onError: () => {},
  })

  const {
    createMarketingPackageTemplate,
    createMarketingPackageTemplatePending,
  } = useCreateMarketingPackageTemplate({
    onSuccess: async () => {
      setIsFormDirty(false)
      await Promise.allSettled([
        invalidateUseStaffGetMarketingPackages(agencyId),
        invalidateUseStaffGetMarketingPackageTemplates(agencyId),
      ])
      onBack()
    },
    onError: () => {},
  })

  const {
    deleteMarketingPackageTemplate,
    deleteMarketingPackageTemplatePending,
  } = useDeleteMarketingPackageTemplate({
    onSuccess: async () => {
      await Promise.allSettled([
        invalidateUseStaffGetMarketingPackages(agencyId),
        invalidateUseStaffGetMarketingPackageTemplates(agencyId),
      ])
      onBack()
    },
    onError: () => {},
  })

  useEffect(() => {
    checkSelectAllState(standardItems, inclusionsList, InclusionsType.STANDARD)
  }, [standardItems])

  useEffect(() => {
    checkSelectAllState(addons, addonsList, InclusionsType.ADDONS)
  }, [addons])

  useEffect(() => {
    if (standardItemsSelectAll) {
      setStandardItems([
        ...inclusionsList.filter((i) => i.id !== SELECT_ALL_LABEL),
      ])
    } else {
      setStandardItems([])
    }
  }, [standardItemsSelectAll])

  useEffect(() => {
    if (addonsSelectAll) {
      setAddons([...addonsList.filter((i) => i.id !== SELECT_ALL_LABEL)])
    } else {
      setAddons([])
    }
  }, [addonsSelectAll])

  const resolvedInitialState = useMemo(() => {
    const resolvedStandardItems: MarketingItemTemplateModel[] =
      includedStandardInclusions
        .map((inclusion) => allItems.find((item) => item.id === inclusion))
        .filter((item) => item !== undefined)
        .map((item) => item as MarketingItemTemplateModel)

    if (marketingPackage?.includeRealEstateListing) {
      resolvedStandardItems.splice(0, 0, REA_LISTING)
    }

    if (marketingPackage?.includeDomainListing) {
      resolvedStandardItems.splice(1, 0, DOMAIN_LISTING)
    }

    const resolvedAddons: MarketingItemTemplateModel[] = includedAddons.flatMap(
      (addon) => {
        const item = allItems.find((i) => i.id === addon)
        return item ? [item as MarketingItemTemplateModel] : []
      }
    )

    const packageName = marketingPackage?.name || ''
    const packageDisplayName = marketingPackage?.displayName || ''
    const specialConditions = marketingPackage?.specialConditions || ''
    const userAccessIds = marketingPackage?.assignedAgentIds || []
    const isItemisedPricing = !!marketingPackage?.displayItemisedPricingOnPdf

    return {
      standardItems: resolvedStandardItems,
      addons: resolvedAddons,
      packageName,
      packageDisplayName,
      specialConditions,
      userAccessIds,
      isItemisedPricing,
      snapshot: {
        packageName,
        packageDisplayName,
        specialConditions,
        userAccessIds: [...userAccessIds].sort(),
        isItemisedPricing,
        standardItemIds: resolvedStandardItems
          .filter((i) => !!i.id)
          .map((i) => i.id!),
        addonIds: resolvedAddons.filter((i) => !!i.id).map((i) => i.id!),
      } satisfies MarketingPackageFormSnapshot,
    }
  }, [allItems, includedAddons, includedStandardInclusions, marketingPackage])

  const resolvedInitialStateRef = useRef(resolvedInitialState)
  resolvedInitialStateRef.current = resolvedInitialState

  useEffect(() => {
    const nextState = resolvedInitialStateRef.current

    setStandardItems(nextState.standardItems)
    setAddons(nextState.addons)
    setPackageName(nextState.packageName)
    setPackageDisplayName(nextState.packageDisplayName)
    setSpecialConditions(nextState.specialConditions)
    setUserAccessIds(nextState.userAccessIds)
    setIsItemisedPricing(nextState.isItemisedPricing)
    setInitialFormSnapshot(nextState.snapshot)
    setIsFormDirty(false)
  }, [packageIdentity])

  useEffect(() => {
    if (!initialFormSnapshot) {
      return
    }

    const currentSnapshot: MarketingPackageFormSnapshot = {
      packageName,
      packageDisplayName,
      specialConditions,
      userAccessIds: [...userAccessIds].sort(),
      isItemisedPricing,
      standardItemIds: standardItems.filter((i) => !!i.id).map((i) => i.id!),
      addonIds: addons.filter((i) => !!i.id).map((i) => i.id!),
    }

    setIsFormDirty(!isEqual(initialFormSnapshot, currentSnapshot))
  }, [
    addons,
    initialFormSnapshot,
    isItemisedPricing,
    packageDisplayName,
    packageName,
    specialConditions,
    standardItems,
    userAccessIds,
  ])

  const onDeleteItemHandler = () => {
    setShowDeleteConfirmation(true)
  }

  const onConfirmDelete = useCallback(() => {
    if (marketingPackage?.id) {
      deleteMarketingPackageTemplate({
        agencyId: agencyId,
        marketingPackageId: marketingPackage.id,
      })
    } else {
      onBack()
    }
  }, [marketingPackage?.id, agencyId, deleteMarketingPackageTemplate, onBack])

  const handleOnDragEndStandard = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (active.id !== over?.id) {
        setStandardItems((prev) => {
          const oldIndex = prev.findIndex((item) => item.id === active.id)
          const newIndex = prev.findIndex((item) => item.id === over?.id)
          if (prev !== standardItems) setIsFormDirty(true)
          return arrayMove(prev, oldIndex, newIndex)
        })
      }
    },
    [standardItems, setStandardItems]
  )

  const handleOnDragEndAddons = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (active.id !== over?.id) {
        setAddons((prev) => {
          const oldIndex = prev.findIndex((item) => item.id === active.id)
          const newIndex = prev.findIndex((item) => item.id === over?.id)
          if (prev !== addons) setIsFormDirty(true)
          return arrayMove(prev, oldIndex, newIndex)
        })
      }
    },
    [addons, setAddons]
  )

  const editInclusions = useCallback(
    (inclusionsType: InclusionsType) => {
      if (inclusionsType === InclusionsType.STANDARD) {
        setAddInclusionsTitle(STANDARD_INCLUSIONS_TITLE)
      } else {
        setAddInclusionsTitle(ADDON_INCLUSIONS_TITLE)
      }
      setShowEditInclusions(true)
    },
    [setAddInclusionsTitle, setShowEditInclusions]
  )

  const onSavePackageChanges = useCallback(() => {
    const itemsList: string[] = standardItems
      .filter((i) => !!i.id)
      .map((i) => i.id!)
    const addonsList: string[] = addons.filter((i) => !!i.id).map((i) => i.id!)

    const isReaListing = !!standardItems.find(
      (i) => i.productName === REA_LISTING_LABEL
    )
    const isDomainListing = !!standardItems.find(
      (i) => i.productName === DOMAIN_LISTING_LABEL
    )
    if (marketingPackage?.id) {
      updateMarketingPackageTemplate({
        id: marketingPackage?.id,
        order: marketingPackageOrder,
        agencyId: agencyId,
        name: packageName,
        displayName: packageDisplayName,
        includeRealEstateListing: isReaListing,
        includeDomainListing: isDomainListing,
        displayItemisedPricingOnPdf: isItemisedPricing,
        items: itemsList,
        addOns: addonsList,
        assignedAgentIds: userAccessIds,
        specialConditions: specialConditions,
      })
    } else {
      createMarketingPackageTemplate({
        agencyId: agencyId,
        order: marketingPackageOrder,
        name: packageName,
        displayName: packageDisplayName,
        includeRealEstateListing: isReaListing,
        includeDomainListing: isDomainListing,
        displayItemisedPricingOnPdf: isItemisedPricing,
        items: itemsList,
        addOns: addonsList,
        specialConditions: specialConditions,
        assignedAgentIds: userAccessIds,
      })
    }
  }, [
    addons,
    agencyId,
    createMarketingPackageTemplate,
    isItemisedPricing,
    marketingPackage?.id,
    marketingPackageOrder,
    packageDisplayName,
    packageName,
    specialConditions,
    standardItems,
    updateMarketingPackageTemplate,
    userAccessIds,
  ])

  // Set the select all and indeterminate state based on the inclusions list
  const checkSelectAllState = (
    inclusionItems: MarketingItemTemplateModel[],
    list: MarketingItemTemplateModel[],
    type: InclusionsType
  ) => {
    if (inclusionItems.length === 0) {
      if (type === InclusionsType.STANDARD) {
        setStandardItemsSelectAll(false)
        setIsStandardItemsIndeterminate(false)
      } else {
        setAddonsSelectAll(false)
        setIsAddonsIndeterminate(false)
      }
    } else if (
      inclusionItems.length ===
        list.filter((i) => i.id !== SELECT_ALL_LABEL).length &&
      isEmpty(
        xorWith(
          inclusionItems,
          list.filter((i) => i.id !== SELECT_ALL_LABEL),
          isEqual
        )
      )
    ) {
      if (type === InclusionsType.STANDARD) {
        setStandardItemsSelectAll(true)
        setIsStandardItemsIndeterminate(false)
        setIsAddonsIndeterminate(false)
      } else {
        setAddonsSelectAll(true)
        setIsAddonsIndeterminate(false)
        setIsStandardItemsIndeterminate(false)
      }
    } else {
      type === InclusionsType.STANDARD
        ? setIsStandardItemsIndeterminate(true)
        : setIsAddonsIndeterminate(true)
    }
  }

  const renderEditInclusions = (item: MarketingItemTemplateModel) => {
    let checked = false

    if (item.id === SELECT_ALL_LABEL) {
      checked =
        addInclusionsTitle === STANDARD_INCLUSIONS_TITLE
          ? standardItemsSelectAll
          : addonsSelectAll
    } else {
      if (addInclusionsTitle === STANDARD_INCLUSIONS_TITLE) {
        checked = standardItems.some((s) => s.id === item.id)
      } else {
        checked = addons.some((s) => s.id === item.id)
      }
    }

    const updateInclusionsList = (value: boolean) => {
      const isStandardInclusionsList =
        addInclusionsTitle === STANDARD_INCLUSIONS_TITLE

      // Set the type of inclusions list
      const inclusionsListItems = isStandardInclusionsList
        ? standardItems
        : addons

      if (value) {
        if (item.id === SELECT_ALL_LABEL) {
          addInclusionsTitle === STANDARD_INCLUSIONS_TITLE
            ? setStandardItemsSelectAll(value)
            : setAddonsSelectAll(value)
        } else {
          const alreadyExist = inclusionsListItems.some((i) => i.id === item.id)
          if (!alreadyExist) {
            setIsFormDirty(true)

            isStandardInclusionsList
              ? setStandardItems([...inclusionsListItems, item])
              : setAddons([...inclusionsListItems, item])
          }
        }
      } else {
        if (item.id === SELECT_ALL_LABEL) {
          addInclusionsTitle === STANDARD_INCLUSIONS_TITLE
            ? setStandardItemsSelectAll(value)
            : setAddonsSelectAll(value)
        } else {
          if (
            JSON.stringify(inclusionsListItems) !==
            JSON.stringify(inclusionsListItems.filter((i) => i.id !== item.id))
          ) {
            setIsFormDirty(true)
          }

          const filteredItems = inclusionsListItems.filter(
            (i) => i.id !== item.id
          )

          if (isStandardInclusionsList) setStandardItems(filteredItems)
          else setAddons(inclusionsListItems.filter((i) => i.id !== item.id))
        }
      }
    }

    return (
      <AgxSpacer
        direction="horizontal"
        key={`${item.id}`}
        contentJustification="space-between"
        contentAlignment="center"
        gap="sm"
        padding="sm"
        style={{
          border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-xl)',
        }}
      >
        <AgxCheckbox
          id="chkProductName"
          checked={checked}
          indeterminate={
            item.id === SELECT_ALL_LABEL &&
            (addInclusionsTitle === STANDARD_INCLUSIONS_TITLE
              ? isStandardItemsIndeterminate
              : isAddonsIndeterminate)
          }
          label={item.productName}
          style="label"
          onChange={updateInclusionsList}
        />
        <span>
          {item.pricePerUnit ? `$${item.pricePerUnit.toFixed(2)}` : '---'}
        </span>
      </AgxSpacer>
    )
  }

  const marketingPackageNonDraggableItemClasses = clsx(
    'borderBottomContainer',
    'marketingPackageNonDraggableItem'
  )
  const marketingPackageItemClasses = clsx(
    'dragDropItem',
    'marketingPackageItems'
  )

  const renderReaAndDomainListingItems = (
    item: MarketingItemTemplateModel,
    index: number
  ) => {
    return (
      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        key={`item-${index}`}
        className={marketingPackageNonDraggableItemClasses}
      >
        <div className="thPackageNameContainer__marketing">
          <AgxTypography
            id={`rea-domain-item-${index}`}
            type="body_ui"
            size="small"
            color="var(--text-primary)"
          >
            {item.productName}
          </AgxTypography>
        </div>
        <AgxTypography
          id={`rea-domain-price-${index}`}
          type="body_ui"
          size="small"
          color="var(--text-primary)"
        >
          {item.pricePerUnit ? `$${item.pricePerUnit.toFixed(2)}` : '---'}
        </AgxTypography>
      </AgxSpacer>
    )
  }

  const renderSortableItemsItem = (
    item: MarketingItemTemplateModel,
    index: number
  ) => {
    return (
      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        key={item.id || `dragItem-${index}`}
        id={item.id || `dragItem-${index}`}
        className={marketingPackageItemClasses}
        gap="sm"
      >
        <div className="thPackageNameContainer__marketing">
          <AgxTypography
            id={`sortable-item-${item.id || index}`}
            type="body_ui"
            size="small"
            color="var(--text-primary)"
          >
            {item.productName}
          </AgxTypography>
        </div>
        <div>
          <AgxTypography
            id={`sortable-price-${item.id || index}`}
            type="body_ui"
            size="small"
            color="var(--text-primary)"
          >
            {item.pricePerUnit ? `$${item.pricePerUnit.toFixed(2)}` : '---'}
          </AgxTypography>
        </div>
      </AgxSpacer>
    )
  }

  const sortableItems = standardItems.filter(
    (item) =>
      item.productName !== REA_LISTING_LABEL &&
      item.productName !== DOMAIN_LISTING_LABEL
  )

  const onBackClick = useCallback(() => {
    if (isFormDirty) setForceCheck(true)
    else onBack()
  }, [isFormDirty, onBack])

  const onUserAccessChange = useCallback((value: string[]) => {
    setUserAccessIds(value ?? [])
  }, [])

  return (
    <AgxColumn veryLargeGap extraClasses="growOne">
      <FormPrompt
        hasUnsavedChanges={isFormDirty}
        forceCheck={forceCheck}
        resetForceCheck={(value: boolean) => setForceCheck(value)}
        onBack={onBack}
      />
      <AgxRow veryLargeGap spaceBetween extraClasses="borderBottomContainer">
        <AgxButton
          id="marketing-package-back"
          text="Marketing Package"
          size="large"
          variant="naked"
          onClick={onBackClick}
          leftIcon={<Icon_Arrow direction="left" />}
        />
        <AgxRow veryLargeGap>
          <AgxButton
            id="delete-package"
            text="Delete package"
            size="medium"
            variant="naked"
            danger
            onClick={onDeleteItemHandler}
          />
          <AgxButton
            id="save-package"
            text="Save Changes"
            size="medium"
            variant="primary"
            onClick={onSavePackageChanges}
            disabled={
              !isFormDirty ||
              updateMarketingPackageTemplatePending ||
              createMarketingPackageTemplatePending ||
              deleteMarketingPackageTemplatePending
            }
          />
        </AgxRow>
      </AgxRow>
      <AgxRow veryLargeGap fill>
        <AgxColumn fill extraLargeGap extraClasses="checkboxContainer">
          <AgxTextInput
            key={`package-name-${packageIdentity}`}
            id={`txtPackageName-${packageIdentity}`}
            type="text"
            stretch
            label="Package name"
            hideOptionalText
            defaultValue={packageName}
            onInputValueChange={({ value }: { value: string }) => {
              if (value && packageName !== value) {
                setIsFormDirty(true)
              }
              setPackageName(value)
            }}
          />
          <AgxTextInput
            key={`package-display-name-${packageIdentity}`}
            id={`txtPackageDisplayName-${packageIdentity}`}
            type="text"
            stretch
            label="Display name on PDF"
            hideOptionalText
            placeholder={packageName}
            defaultValue={packageDisplayName}
            onInputValueChange={({ value }: { value: string }) => {
              if (value && packageDisplayName !== value) {
                setIsFormDirty(true)
              }
              setPackageDisplayName(value)
            }}
          />
          <AgxDropdown
            id={`userAccess-${marketingPackage?.id}`}
            label="User Access"
            options={allAgentOptions}
            type="multi"
            hideOptionalText
            selected={userAccessIds}
            onChange={onUserAccessChange}
          />
        </AgxColumn>
        <AgxColumn fill extraLargeGap extraClasses="checkboxContainer">
          <AgxTextArea
            key={`special-conditions-${packageIdentity}`}
            id={`agx-docuSignEmailMessage-${packageIdentity}`}
            label="Special Conditions"
            placeholder="Nil"
            defaultValue={specialConditions}
            onInputValueChange={({ value }: { value: string }) => {
              if (value && specialConditions !== value) {
                setIsFormDirty(true)
              }
              setSpecialConditions(value)
            }}
            rows={6}
            maxLength={925}
            hideOptionalText
            stretch
          />
          <AgxCheckbox
            id="chkDisplayOnPdf"
            checked={isItemisedPricing}
            label="Display itemised pricing on PDF"
            style="label"
            onChange={(value: boolean) => {
              if (isItemisedPricing !== value) {
                setIsFormDirty(true)
              }
              setIsItemisedPricing(value)
            }}
          />
        </AgxColumn>
      </AgxRow>
      <AgxRow veryLargeGap>
        <AgxColumn fill mediumGap extraClasses="sectionStyle">
          <AgxRow spaceBetween extraClasses="borderBottomContainer">
            <AgxTypography
              id="standard-inclusions-title"
              type="body_ui_strong"
              size="medium"
              color="var(--text-primary)"
            >
              Standard inclusions
            </AgxTypography>
            <AgxButton
              id="edit-standard-inclusions"
              text="Edit Inclusions"
              size="medium"
              variant="hollow"
              onClick={() => editInclusions(InclusionsType.STANDARD)}
            />
          </AgxRow>
          {standardItems.length === 0 ? (
            <AgxRow>
              <AgxTypography
                id="standard-inclusions-copy"
                type="body_ui"
                size="medium"
                color="var(--text-primary)"
              >
                These will be ticked by default but can be removed by the agent
                when they modify the package.
              </AgxTypography>
            </AgxRow>
          ) : null}
          {standardItems
            .filter(
              (item) =>
                item.productName === REA_LISTING_LABEL ||
                item.productName === DOMAIN_LISTING_LABEL
            )
            .map((item, index) => renderReaAndDomainListingItems(item, index))}
          <AgxSortableList
            items={sortableItems}
            getId={(item) => item.id || `editItem-${item.id}`}
            renderItem={(item, index) => renderSortableItemsItem(item, index)}
            onDragEnd={handleOnDragEndStandard}
          />
        </AgxColumn>
        <AgxColumn mediumGap fill extraClasses="sectionStyle">
          <AgxRow spaceBetween extraClasses="borderBottomContainer">
            <AgxTypography
              id="optional-addons-title"
              type="body_ui_strong"
              size="medium"
              color="var(--text-primary)"
            >
              Optional Add-ons
            </AgxTypography>
            <AgxButton
              id="edit-addon-inclusions"
              text="Edit Inclusions"
              size="medium"
              variant="hollow"
              onClick={() => editInclusions(InclusionsType.ADDONS)}
            />
          </AgxRow>
          {addons.length === 0 ? (
            <AgxRow>
              <AgxTypography
                id="optional-addons-copy"
                type="body_ui"
                size="medium"
                color="var(--text-primary)"
              >
                These will be displayed unchecked by default to the agent in a
                separate section for upselling.
              </AgxTypography>
            </AgxRow>
          ) : null}
          <AgxSortableList
            items={addons}
            getId={(addon) => addon.id || `addOnItem-${addon.id}`}
            renderItem={(item, index) => renderSortableItemsItem(item, index)}
            onDragEnd={handleOnDragEndAddons}
            extraClasses="marketingPackageListItems"
          />
        </AgxColumn>
      </AgxRow>
      <DeleteConfirmation
        name={marketingPackage?.name || ''}
        title="Delete marketing package?"
        lineOne="Are you sure you want to delete"
        lineTwo="This cannot be undone."
        primaryActionTitle="Permanently delete package"
        secondaryActionTitle="No, cancel"
        showPopUp={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => onConfirmDelete()}
      />

      <Popup
        title={addInclusionsTitle}
        isOpen={showEditInclusions}
        onClose={() => setShowEditInclusions(false)}
        actionButtons={[
          {
            id: 'update-package',
            title: 'Update package',
            isPrimary: true,
            clickHandler: () => setShowEditInclusions(false),
          },
          {
            id: 'cancel-edit-inclusions',
            title: 'Cancel',
            clickHandler: () => setShowEditInclusions(false),
          },
        ]}
        size={{ widthInPX: 640 }}
      >
        <AgxColumn veryLargeGap>
          <AgxRow extraClasses="borderBottomContainer">
            <AgxTextInput
              id="txtSearch"
              type="text"
              placeholder="Search"
              noHeader
              stretch
              onInputValueChange={({ value }: { value: string }) =>
                console.log(value)
              }
              leftIcon={<Icon_MagnifyingGlass />}
            />
          </AgxRow>
          {addInclusionsTitle === STANDARD_INCLUSIONS_TITLE
            ? inclusionsList.map((item) => renderEditInclusions(item))
            : addonsList.map((item) => renderEditInclusions(item))}
        </AgxColumn>
      </Popup>
    </AgxColumn>
  )
}

export default EditMarketingPackage
